import { localCalendarDateIso } from '../../utils/datetime';
import { newestPeriodFirst } from './newestPeriodFirst';

/**
 * Pipeline trend tables default to periods with activity, plus today.
 * API windows and CSV exports stay complete; this only changes the on-screen rows.
 * Ordering stays newest-period-first so the shared report header and these tables match.
 */

export type CollapseEmptyHistoryOptions<T> = {
  periodKey: (row: T) => string;
  /** Exclusive period end when the row is a range (daily next-day, weekly next-week). */
  periodEndKey?: (row: T) => string | null | undefined;
  hasActivity: (row: T) => boolean;
  showEmptyDays: boolean;
  /** Pin "today" in tests. The UI uses the browser calendar date plus the report's UTC day. */
  todayIso?: string;
  generatedAt?: string | null;
};

export function channelSyncTrendHasActivity(row: {
  success_count: number;
  failed_count: number;
  other_count: number;
}): boolean {
  return row.success_count + row.failed_count + row.other_count > 0;
}

export function outboxRollupHasActivity(row: {
  queued_volume: number;
  pending_count: number;
  dispatch_success: number;
  dispatch_failed: number;
  retry_total: number;
  retry_exhaustions: number;
}): boolean {
  return (
    row.queued_volume > 0
    || row.pending_count > 0
    || row.dispatch_success > 0
    || row.dispatch_failed > 0
    || row.retry_total > 0
    || row.retry_exhaustions > 0
  );
}

export function reconciliationTrendHasActivity(row: { total_discrepancies: number }): boolean {
  return row.total_discrepancies > 0;
}

export function fanoutDailyHasActivity(row: {
  total_breach_windows: number;
  counter_deltas: {
    retry_exhaustion_total: number;
    oversell_exception_total: number;
  };
}): boolean {
  return (
    row.total_breach_windows > 0
    || row.counter_deltas.retry_exhaustion_total > 0
    || row.counter_deltas.oversell_exception_total > 0
  );
}

export function collapseEmptyHistory<T>(
  rows: readonly T[],
  options: CollapseEmptyHistoryOptions<T>,
): T[] {
  const ordered = newestPeriodFirst(rows, options.periodKey);
  if (options.showEmptyDays) {
    return ordered;
  }

  const activeDays = new Set<string>();
  for (const row of rows) {
    if (options.hasActivity(row)) {
      activeDays.add(calendarDayKey(options.periodKey(row)));
    }
  }

  const anchors = currentDayAnchors(options);
  return ordered.filter((row) => {
    const start = options.periodKey(row);
    if (activeDays.has(calendarDayKey(start))) {
      return true;
    }
    const end = options.periodEndKey?.(row);
    return anchors.some((day) => periodCoversDay(start, end, day));
  });
}

function currentDayAnchors<T>(options: CollapseEmptyHistoryOptions<T>): string[] {
  if (options.todayIso) {
    return [options.todayIso];
  }
  const anchors = [localCalendarDateIso()];
  const generatedDay = utcDatePrefix(options.generatedAt);
  if (generatedDay && !anchors.includes(generatedDay)) {
    anchors.push(generatedDay);
  }
  return anchors;
}

function calendarDayKey(period: string): string {
  return period.slice(0, 10);
}

function utcDatePrefix(instant: string | null | undefined): string | null {
  if (!instant) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(instant);
  return match?.[1] ?? null;
}

/** True when `day` (YYYY-MM-DD) falls in [start, end). A missing end is a single day. */
function periodCoversDay(
  periodStart: string,
  periodEnd: string | null | undefined,
  day: string,
): boolean {
  const startDay = calendarDayKey(periodStart);
  if (!periodEnd) {
    return startDay === day;
  }
  const endDay = calendarDayKey(periodEnd);
  if (endDay <= startDay) {
    return startDay === day;
  }
  return startDay <= day && day < endDay;
}
